import React from 'react';

interface ColumnSelectorProps {
  columns: string[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  disabled?: boolean;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({ 
  columns, 
  selectedColumns, 
  onColumnsChange, 
  disabled = false 
}) => {
  const handleColumnToggle = (column: string) => {
    if (disabled) return;
    
    if (selectedColumns.includes(column)) {
      onColumnsChange(selectedColumns.filter(col => col !== column));
    } else {
      onColumnsChange([...selectedColumns, column]);
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    
    if (selectedColumns.length === columns.length) {
      onColumnsChange([]);
    } else {
      onColumnsChange(columns);
    }
  };

  const allSelected = selectedColumns.length === columns.length;
  const noneSelected = selectedColumns.length === 0;

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6">
      {/* Column Stats */}
      <div className="neon-panel p-6">
        <div className="neon-panel-header">
          Column Statistics
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 font-mono text-sm">
          <div className="text-center">
            <div className="text-gray-400 mb-2">Total:</div>
            <div className="text-neon-blue text-lg">{columns.length}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Selected:</div>
            <div className="text-neon-green text-lg">{selectedColumns.length}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Available:</div>
            <div className="text-neon-yellow text-lg">{columns.length - selectedColumns.length}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Status:</div>
            <div className={`text-lg ${noneSelected ? 'text-red-400' : 'text-neon-green'}`}>
              {noneSelected ? '[EMPTY]' : '[READY]'}
            </div>
          </div>
        </div>
      </div>

      {/* Column Selection Controls */}
      <div className="neon-panel">
        <div className="neon-panel-header">
          Column Selection Manager
        </div>
        <div className="p-6 space-y-6">
          {/* Action Buttons */}
          <div className="flex gap-4 justify-center">
            <button
              onClick={handleSelectAll}
              disabled={disabled}
              className={`neon-button neon-button-blue px-6 py-3 ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={() => onColumnsChange([])}
              disabled={disabled || noneSelected}
              className={`neon-button neon-button-yellow px-6 py-3 ${
                disabled || noneSelected ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Clear Selection
            </button>
          </div>

          {/* Column Grid */}
          <div className="neon-panel p-4">
            <div className="neon-panel-header">
              Available Columns
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
              {columns.map((column, index) => {
                const isSelected = selectedColumns.includes(column);
                return (
                  <label
                    key={column}
                    className={`
                      neon-panel p-3 cursor-pointer transition-all duration-200 flex items-center space-x-3
                      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                      ${isSelected ? 'bg-neon-purple bg-opacity-20 border-neon-purple' : 'hover:bg-neon-cyan hover:bg-opacity-10'}
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleColumnToggle(column)}
                      disabled={disabled}
                      className="neon-checkbox"
                    />
                    <div className="flex-1">
                      <div className="font-mono text-sm text-white truncate">{column}</div>
                      <div className="font-mono text-xs text-gray-400">
                        Column #{index + 1}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Selection Summary */}
          {selectedColumns.length > 0 && (
            <div className="neon-panel p-4">
              <div className="neon-panel-header">
                Selected Columns Preview
              </div>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {selectedColumns.map((column) => (
                  <span
                    key={column}
                    className="inline-flex items-center gap-2 bg-neon-purple bg-opacity-20 border border-neon-purple text-neon-purple px-3 py-1 rounded font-mono text-sm"
                  >
                    {column}
                    <button
                      onClick={() => handleColumnToggle(column)}
                      disabled={disabled}
                      className="text-neon-red hover:text-red-400 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="font-mono text-xs text-gray-500 text-center space-y-1">
            <div>• Click checkboxes to select/deselect columns</div>
            <div>• Use action buttons for bulk operations</div>
            <div>• Selected columns will be included in the SQL table</div>
            {noneSelected && (
              <div className="text-red-400 mt-2">⚠️ Please select at least one column to proceed</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnSelector; 